import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Send, MessageCircle } from 'lucide-react';
import { useSite } from '../context/SiteContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1" aria-label="a escrever">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-fl-yellow"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}

export default function ChatWidget() {
  const { t, lang } = useSite();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', text: t.chat.welcome }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, minimized]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput('');
    const next = [...messages, { role: 'user', text: content }];
    setMessages([...next, { role: 'assistant', text: '' }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: next.slice(-12).map((m) => ({ role: m.role, content: m.text })),
          lang,
        }),
      });
      if (res.status === 429) throw new Error('rate');
      if (!res.ok || !res.body) throw new Error('offline');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new Error('offline');
            if (parsed.t) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', text: copy[copy.length - 1].text + parsed.t };
                return copy;
              });
            }
          } catch (e) {
            if (e.message === 'offline') throw e;
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: 'assistant',
          text: e.message === 'rate' ? t.chat.rateLimited : t.chat.offline,
        };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            onClick={() => setOpen(true)}
            aria-label={t.chat.open}
            data-testid="chat-open-button"
            className="group fixed bottom-5 right-5 z-[100] flex h-16 w-16 items-center justify-center rounded-full bg-black shadow-[0_0_0_1px_rgba(255,206,0,0.35),0_10px_35px_rgba(0,0,0,0.65)] transition-shadow duration-300 hover:shadow-[0_0_0_2px_#FFCE00,0_10px_40px_rgba(255,206,0,0.3)] sm:h-[72px] sm:w-[72px]"
          >
            <motion.img
              src="/assets/iris-anim.webp"
              alt=""
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="h-12 w-12 object-contain transition-transform duration-500 group-hover:rotate-12 sm:h-14 sm:w-14"
            />
            <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-black bg-fl-yellow" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 28, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-3 left-3 right-3 z-[105] flex max-h-[75vh] flex-col overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-950/95 shadow-[0_24px_70px_rgba(0,0,0,0.75)] backdrop-blur-xl sm:bottom-6 sm:left-auto sm:right-6 sm:w-[400px]"
            role="dialog"
            aria-label={t.chat.title}
            data-testid="chat-window"
          >
            <div className="flex items-center gap-3 border-b border-zinc-800/80 bg-black/70 px-4 py-3.5">
              <span className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-black ring-2 ring-fl-yellow/50 sm:h-16 sm:w-16">
                <img src="/assets/iris-anim.webp" alt="Íris" className="h-12 w-12 object-contain sm:h-14 sm:w-14" />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-black bg-fl-yellow" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-base font-bold tracking-wide text-white">{t.chat.title}</p>
                <p className="truncate font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{t.chat.subtitle}</p>
              </div>
              <button
                onClick={() => setMinimized(!minimized)}
                aria-label={t.chat.minimize}
                data-testid="chat-minimize"
                className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-fl-yellow"
              >
                <Minus className="h-4 w-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label={t.chat.close}
                data-testid="chat-close"
                className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-fl-yellow"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {!minimized && (
              <>
                <div
                  ref={listRef}
                  data-lenis-prevent
                  className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4 [touch-action:pan-y]"
                  data-testid="chat-messages"
                >
                  {messages.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      className={`flex items-end gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {m.role === 'assistant' && (
                        <img src="/assets/iris-mascot.png" alt="" className="h-7 w-7 shrink-0 object-contain" />
                      )}
                      <div
                        className={`max-w-[82%] px-4 py-3 text-sm leading-relaxed ${
                          m.role === 'user'
                            ? 'rounded-2xl rounded-br-md bg-fl-yellow font-medium text-black'
                            : 'rounded-2xl rounded-bl-md border border-zinc-800 bg-zinc-900/80 text-zinc-100'
                        }`}
                        data-testid={`chat-message-${i}`}
                      >
                        {m.text === '' && busy && i === messages.length - 1 ? (
                          <TypingDots />
                        ) : (
                          m.text.replace(/\*\*/g, '')
                        )}
                      </div>
                    </motion.div>
                  ))}

                  {messages.length <= 1 && (
                    <div className="flex flex-wrap gap-2 pt-2" data-testid="chat-suggestions">
                      {t.chat.suggestions.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => send(s)}
                          data-testid={`chat-suggestion-${i}`}
                          className="rounded-full border border-zinc-700 px-3.5 py-2 text-left text-xs text-zinc-300 transition-all duration-300 hover:border-fl-yellow hover:bg-fl-yellow/10 hover:text-fl-yellow"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <p className="px-4 pb-1 text-center text-[10px] leading-snug text-zinc-600" data-testid="chat-disclaimer">
                  {t.chat.disclaimer}
                </p>

                <a
                  href="https://wa.me/351915562805"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="chat-whatsapp"
                  className="mx-auto mb-1 flex w-fit items-center gap-1.5 rounded-full border border-zinc-800 px-3 py-1.5 text-[10px] font-medium text-zinc-400 transition-colors duration-300 hover:border-fl-yellow hover:text-fl-yellow"
                >
                  <MessageCircle className="h-3 w-3" />
                  {t.chat.whatsapp}
                </a>

                <form
                  className="flex items-center gap-2 p-3 pt-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                >
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t.chat.placeholder}
                    maxLength={800}
                    aria-label={t.chat.placeholder}
                    data-testid="chat-input"
                    className="min-w-0 flex-1 rounded-full border border-zinc-800 bg-black px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-fl-yellow"
                  />
                  <button
                    type="submit"
                    disabled={busy || !input.trim()}
                    aria-label={t.chat.send}
                    data-testid="chat-send"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-fl-yellow text-black transition-all duration-300 hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
